import Close from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import {
	useGetAdmissionRequestDetailQuery,
	useGetMemberListQuery,
} from '@/api/member-management/admissionRequest';
import { MemberStatusEnum } from '@/api/enum/common.enum';
import {
	DefaultValues,
	FormProvider,
	useFieldArray,
	useForm,
} from 'react-hook-form';
import AppSummarySearchBar from '@/common/components/form-inputs/AppSummarySearchBar';
import { useNavigate, useParams } from 'react-router-dom';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import {
	useAddFamilyMemberToListMutation,
	useGetFamilyDetailQuery,
	useGetFamilyListQuery,
} from '@/api/family-management/familyApi';
import { closeSnackbar, enqueueSnackbar } from 'notistack';

import CloseIcon from '@mui/icons-material/Close';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { showLabel } from '@/utils/helper';
import { YESNO, ZH_YESNO } from '@/common/constants/common';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import dayjs from 'dayjs';
import { DISPLAY_DATE_FORMAT } from '@/utils/dateHelper';
import { useGetDicListQuery } from '@/api/common';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';

interface Props {
	details: any;
	groupDetails: any;
}

const AddFamilyMemberToListDialog = (props: Props) => {
	const { details, groupDetails } = props;
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [otherOpen, setOtherOpen] = useState(false);
	const showModal = useModal();
	const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

	const [selectedRow, setSelectedRow] = useState<any>({});
	const [addFamilyMemberToList] = useAddFamilyMemberToListMutation();
	const { admissionRequestId } = useParams();

	const { data: selectDetails } = useGetFamilyDetailQuery(selectedRowId || '', {
		refetchOnMountOrArgChange: true,
		skip: !selectedRowId || !otherOpen,
	});

	const { data: relationshipDicList } = useGetDicListQuery('relationship', {
		refetchOnMountOrArgChange: true,
	});

	const defaultValues: DefaultValues<any> = {
		pagination: { ...initialPagination, size: 5 },
	};
	const methods = useForm<any>({
		defaultValues,
	});

	const watchSearchString = methods.watch('searchString');
	const watchPagination = methods.watch('pagination');

	// Reset to first page whenever the search string changes.
	useEffect(() => {
		methods.setValue('pagination', {
			...methods.getValues('pagination'),
			page: 1,
		});
	}, [watchSearchString]);

	const { data: memberList, isFetching: isMemberListFetching } =
		useGetFamilyListQuery(
			{
				sortings: [],
				pagination: watchPagination,
				searchString: watchSearchString,
				excludeMember: details?.data.id,
				statusList: [0],
			},
			{
				refetchOnMountOrArgChange: true,
				skip: !open,
			}
		);

	useEffect(() => {
		methods2.setValue(
			'familyMemberApplicationRelationshipList',
			groupDetails?.data
		);
	}, [groupDetails, otherOpen]);

	const methods2 = useForm<any>({});
	const { fields: targetFields }: any = useFieldArray({
		control: methods2.control,
		name: 'targetList',
	});
	const { fields: familyGroupFields }: any = useFieldArray({
		control: methods2.control,
		name: 'familyMemberApplicationRelationshipList',
	});

	useEffect(() => {
		methods2.setValue('targetList', [details?.data]);
		console.log(details?.data);
	}, [details, otherOpen]);

	const handleClose = () => {
		setOpen(false);
	};

	const onSubmit = async (data: any) => {
		if (selectedRowId) setOtherOpen(true);
	};
	const onSubmit2 = async (data: any) => {
		console.log(data);
		const response = await addFamilyMemberToList({
			sourceMemberType: 1,
			sourceMemberId: selectedRowId || '',
			relationshipList: [
				...(data.targetList || []).map((item: any) => {
					return {
						targetMemberType: 0,
						targetMemberId: item.id,
						relationshipToAddedPerson: item.relationshipToAddedPerson,
						relationshipToMember: item.relationshipToMember,
					};
				}),
				...(data.familyMemberApplicationRelationshipList || []).map(
					(item: any) => {
						return {
							targetMemberType: item.targetMemberType,
							targetMemberId: item.targetMemberId,
							relationshipToAddedPerson: item.relationshipToAddedPerson,
							relationshipToMember: item.relationshipToMember,
						};
					}
				),
			],
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.addFamilyMemberToListSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			setOpen(false);
			setOtherOpen(false);
		}
	};

	const columns: GridColDef[] = [
		{
			field: 'familyMemberFileNo',
			headerName: t('family.familyMemberFileNo'),
			flex: 15,
		},
		{
			field: 'nameEn',
			headerName: t('member.name'),
			flex: 15,
			renderCell: (params) => (
				<Box>
					<Typography variant='body2'>{params.row.nameEn}</Typography>
					<Typography variant='body2'>{params.row.nameZh}</Typography>
				</Box>
			),
		},
		{
			field: 'phone',
			headerName: t('member.mobilePhone'),
			flex: 15,
		},
		{
			field: 'familyMemberStatus',
			headerName: t('member.status'),
			flex: 15,
			maxWidth: 140,
			minWidth: 140,
			renderCell: (params) => (
				<Chip
					color={
						params.row.familyMemberStatus == 1 ? 'default' : 'chipTertiary'
					}
					sx={{
						height: 'auto',
						minHeight: '24px',
						maxWidth: '100%',
						overflow: 'hidden',
						whiteSpace: 'normal',
						wordBreak: 'break-word',
						padding: '4px 4px',
					}}
					label={
						<Box
							sx={{
								width: '100%',
								whiteSpace: 'normal',
								wordBreak: 'break-word',
							}}
						>
							{params.row.familyMemberStatusDesc}
						</Box>
					}
				/>
			),
		},
		{
			field: 'checkbox',
			headerName: '',
			flex: 15,
			renderCell: (params) => (
				<Checkbox
					checked={params.row.id === selectedRowId}
					onChange={() => {
						// Toggle: clicking the already-selected row unchecks it.
						setSelectedRowId(
							params.row.id === selectedRowId ? null : params.row.id
						);
						setSelectedRow(params.row.id === selectedRowId ? {} : params.row);
					}}
				/>
			),
		},
	];

	const columns2: GridColDef[] = [
		{
			field: 'nameEn',
			headerName: t('member.name_en'),
			flex: 15,
		},
		{
			field: 'nameZh',
			headerName: t('member.name_zh'),
			flex: 15,
		},
		{
			field: 'relationshipToMember',
			headerName: t('family.relationshipAddedPersonToMember'),
			flex: 15,
			renderCell: (params) => {
				const index = params.row.index;
				return (
					<AppDropDownMenu
						control={methods2.control}
						name={`targetList[${index}].relationshipToMember`}
						label={t('family.relationshipAddedPersonToMember')}
						options={(relationshipDicList?.data || []).map((item: any) => ({
							...item,
							name: item.labelEn,
						}))}
					/>
				);
			},
		},

		{
			field: 'relationshipToAddedPerson',
			headerName: t('family.relationshipMemberToAddedPerson'),
			flex: 15,
			renderCell: (params) => {
				const index = params.row.index;
				return (
					<AppDropDownMenu
						control={methods2.control}
						name={`targetList[${index}].relationshipToAddedPerson`}
						label={t('family.relationshipMemberToAddedPerson')}
						options={(relationshipDicList?.data || []).map((item: any) => ({
							...item,
							name: item.labelEn,
						}))}
					/>
				);
			},
		},
		{
			field: 'phone',
			headerName: t('preAdmission.mobileNo'),
			flex: 15,
		},
		{
			field: 'fileNo',
			headerName: t('family.memberFileNo'),
			flex: 15,
		},

		{
			field: 'familyMemberFileNo',
			headerName: t('family.familyMemberFileNo'),
			flex: 15,
		},
		{
			field: 'membershipRole',
			headerName: t('family.membershipRole'),
			flex: 15,
		},
		{
			field: 'isCase',
			headerName: t('member.isCase'),
			flex: 15,
		},
	];

	const columns3: GridColDef[] = [
		{
			field: 'targetMemberNameEn',
			headerName: t('member.name_en'),
			flex: 15,
		},
		{
			field: 'targetMemberNameZh',
			headerName: t('member.name_zh'),
			flex: 15,
		},
		{
			field: 'relationshipToMember',
			headerName: t('family.relationshipAddedPersonToMember'),
			flex: 15,
			renderCell: (params) => {
				const index = params.row.index;
				return (
					<AppDropDownMenu
						control={methods2.control}
						name={`familyMemberApplicationRelationshipList[${index}].relationshipToMember`}
						label={t('family.relationshipAddedPersonToMember')}
						options={(relationshipDicList?.data || []).map((item: any) => ({
							...item,
							name: item.labelEn,
						}))}
					/>
				);
			},
		},

		{
			field: 'relationshipToAddedPerson',
			headerName: t('family.relationshipMemberToAddedPerson'),
			flex: 15,
			renderCell: (params) => {
				const index = params.row.index;
				return (
					<AppDropDownMenu
						control={methods2.control}
						name={`familyMemberApplicationRelationshipList[${index}].relationshipToAddedPerson`}
						label={t('family.relationshipMemberToAddedPerson')}
						options={(relationshipDicList?.data || []).map((item: any) => ({
							...item,
							name: item.labelEn,
						}))}
					/>
				);
			},
		},
		{
			field: 'targetMemberPhone',
			headerName: t('preAdmission.mobileNo'),
			flex: 15,
		},
		{
			field: 'targetMemberFileNo',
			headerName: t('family.memberFileNo'),
			flex: 15,
		},

		{
			field: 'targetFamilyMemberFileNo',
			headerName: t('family.familyMemberFileNo'),
			flex: 15,
		},
		{
			field: 'targetFamilyMembership',
			headerName: t('family.membershipRole'),
			flex: 15,
		},
		{
			field: 'isCase',
			headerName: t('member.isCase'),
			flex: 15,
			renderCell: (params) => showLabel(params.row.isCase, YESNO),
		},
	];

	return (
		<>
			<Button
				variant='outlined'
				endIcon={<GroupAddOutlinedIcon />}
				onClick={() => {
					setOpen(true);
				}}
				style={{ alignItems: 'center', marginLeft: '8px' }}
			>
				{t('family.addFamilyMemberToList')}
			</Button>
			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='lg'
				fullWidth
				onKeyDown={(e) => {
					if (e.key === 'Tab') {
						e.stopPropagation();
					}
				}}
			>
				<DialogTitle variant='h5'>
					{t('family.linkExistingFamilyMember')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<Typography variant='body2' sx={{ marginBottom: '12px' }}>
						{t('family.linkExistingFamilyMemberText')}
					</Typography>
					<FormProvider {...methods}>
						<Grid
							container
							spacing={2}
							marginTop={0}
							marginBottom={2}
							alignItems='center'
						>
							<Grid size={12}>
								<AppSummarySearchBar
									name='searchString'
									control={methods.control}
									placeholder={t('common.search')}
								/>
							</Grid>
							<Grid size={12}>
								<AppDataGrid
									loading={isMemberListFetching}
									rows={memberList?.data.items || []}
									rowCount={memberList?.data.pagination.totalItems ?? 0}
									columns={columns}
									getRowId={(row) => row.id}
									control={methods.control}
								/>
							</Grid>
						</Grid>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button sx={{ textTransform: 'none' }} onClick={() => handleClose()}>
						{t('button.cancel')}
					</Button>
					<Button
						type='submit'
						variant='contained'
						disabled={!selectedRowId}
						onClick={onSubmit}
					>
						{t('button.next')}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={otherOpen}
				onClose={() => setOtherOpen(false)}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='xl'
				fullWidth
				onKeyDown={(e) => {
					if (e.key === 'Tab') {
						e.stopPropagation();
					}
				}}
			>
				<DialogTitle variant='h5'>
					{t('family.addFamilyMemberToList')}
				</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => setOtherOpen(false)}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<FormProvider {...methods2}>
					<form onSubmit={methods2.handleSubmit(onSubmit2)}>
						<DialogContent>
							<Typography variant='body2' sx={{ marginBottom: '12px' }}>
								{t('family.familyMemberInformationAddPerson')}
							</Typography>

							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid size={6}>
									<AppFieldView
										label={t('member.name_en')}
										value={selectDetails?.data.nameEn}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('member.name_zh')}
										value={selectDetails?.data.nameZh}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('family.memberFileNo')}
										value={selectDetails?.data.fileNo}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('family.familyMemberFileNo')}
										value={selectDetails?.data.familyMemberFileNo}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('family.membershipRole')}
										value={selectDetails?.data.membershipRole}
									/>
								</Grid>
								<Grid size={6}>
									<AppFieldView
										label={t('member.isCase')}
										value={showLabel(selectDetails?.data.isCase, YESNO)}
									/>
								</Grid>

								<Grid size={12}>
									<Typography variant='body1'>{t('family.member')}</Typography>
								</Grid>

								<Grid size={12} display='flex'>
									<LocalDataGrid
										rows={(methods2.getValues('targetList') || []).map(
											(item: any, index: any) => ({
												...item,
												index,
											})
										)}
										columns={columns2}
										getRowId={(row) => row.id}
										pagination={false}
									/>
								</Grid>
								<Grid size={12}>
									<Typography variant='body1'>
										{t('family.otherMembersOrFamilyMembers')}
									</Typography>
								</Grid>
								<Grid size={12} display='flex'>
									<LocalDataGrid
										rows={(
											methods2.getValues(
												'familyMemberApplicationRelationshipList'
											) || []
										).map((item: any, index: any) => ({
											...item,
											index,
										}))}
										columns={columns3}
										getRowId={(row) => row.id}
										pagination={false}
									/>
								</Grid>
							</Grid>
						</DialogContent>
						<DialogActions>
							<Button
								sx={{ textTransform: 'none' }}
								onClick={() => setOtherOpen(false)}
							>
								{t('button.cancel')}
							</Button>
							<Button type='submit' variant='contained'>
								{t('button.confirm')}
							</Button>
						</DialogActions>
					</form>
				</FormProvider>
			</Dialog>
		</>
	);
};

export default AddFamilyMemberToListDialog;
